// SWAP POINT: the canned recipe corpus behind every mock in this folder.
// Stage 1 kept three recipes inline in parseInput; Stage 2 needs a wider set so
// history-first search has something to actually find, and so the online
// fallback has somewhere to fall back to. Replace this with real data sources;
// keep the record shape, since the library, the matcher and both mocks all
// read it.
//
// Record shape (the canonical recipe):
//   id            stable slug
//   dishName      display name
//   servings      number
//   totalMinutes  REQUIRED — the only timing figure we rely on
//   prepMinutes   optional, omitted when unknown. Never invented.
//   cookMinutes   optional, same rule.
//   ingredients   [{ name, quantity, quantityMissing? }] — ALL of them, oil and
//                 salt included, because ingredient count is a number we show
//                 and rank on. quantityMissing marks an amount the source never
//                 gave; "to taste" is an amount, not a gap. Catalog entries
//                 never set it — extraction does.
//   steps         [string]
//   unclearSteps  optional [index] — steps extraction could not read cleanly.
//                 Indexes rather than objects so the catalog's steps stay plain
//                 strings; the working copy in OrderContext pairs them up.
//   origin        { kind: 'text' | 'video' | 'web', label, url?, creator?,
//                 siteName? }
//                 Permanent provenance AND attribution — one place, so the link
//                 and the credit can never disagree. NOT how the recipe got
//                 picked today; that is derived per search and never stored.
//   media         optional { videoUrl } — the ONLY thing that decides whether a
//                 player renders. A 'web' recipe can carry video too. Must be a
//                 durable address: never an object URL, which dies with the tab.
//
// Stage 3 note: `servings` and `totalMinutes` may be null on recipes that came
// from a paste, where the source simply never said. Screens render the gap.

export const RECIPE_CATALOG = [
  {
    id: 'salmon-rice-bowl',
    dishName: 'Grilled salmon rice bowl',
    servings: 2,
    totalMinutes: 30,
    prepMinutes: 10,
    cookMinutes: 20,
    ingredients: [
      { name: 'Salmon fillets', quantity: '2' },
      { name: 'Jasmine rice', quantity: '1 cup' },
      { name: 'Cucumber', quantity: '1' },
      { name: 'Avocado', quantity: '1' },
      { name: 'Edamame, shelled', quantity: '1 cup' },
      { name: 'Soy sauce', quantity: '3 tbsp' },
      { name: 'Rice vinegar', quantity: '1 tbsp' },
      { name: 'Sesame oil', quantity: '1 tsp' },
      { name: 'Sesame seeds', quantity: '1 tbsp' },
      { name: 'Olive oil', quantity: '1 tbsp' },
      { name: 'Salt', quantity: '1 tsp' },
    ],
    steps: [
      'Rinse the rice and start it cooking.',
      'Pat the salmon dry, then oil and salt both sides.',
      'Grill the salmon 4 minutes a side until it flakes.',
      'Slice the cucumber and avocado.',
      'Whisk soy sauce, rice vinegar and sesame oil into a dressing.',
      'Build the bowls over rice and finish with sesame seeds.',
    ],
    origin: { kind: 'web', label: 'Weeknight Kitchen', url: 'https://recipes.example.com/salmon-rice-bowl' },
  },
  {
    id: 'beef-tacos',
    dishName: 'Weeknight beef tacos',
    servings: 4,
    totalMinutes: 25,
    prepMinutes: 10,
    cookMinutes: 15,
    ingredients: [
      { name: 'Ground beef', quantity: '1 lb' },
      { name: 'Corn tortillas', quantity: '12 ct' },
      { name: 'Yellow onion', quantity: '1' },
      { name: 'Garlic', quantity: '3 cloves' },
      { name: 'Chili powder', quantity: '1 tbsp' },
      { name: 'Ground cumin', quantity: '2 tsp' },
      { name: 'Smoked paprika', quantity: '1 tsp' },
      { name: 'Cheddar, shredded', quantity: '8 oz' },
      { name: 'Lime', quantity: '2' },
      { name: 'Neutral oil', quantity: '1 tbsp' },
      { name: 'Salt', quantity: '1 tsp' },
    ],
    steps: [
      'Dice the onion and mince the garlic.',
      'Brown the beef with the onion for 8 minutes.',
      'Stir in the garlic and spices, then simmer 5 minutes.',
      'Warm the tortillas in a dry pan.',
      'Assemble with cheddar and a squeeze of lime.',
    ],
    origin: { kind: 'text', label: 'your description' },
  },
  {
    id: 'veggie-pad-thai',
    dishName: 'Veggie pad thai',
    servings: 2,
    totalMinutes: 30,
    ingredients: [
      { name: 'Rice noodles', quantity: '8 oz' },
      { name: 'Firm tofu', quantity: '14 oz' },
      { name: 'Bean sprouts', quantity: '2 cups' },
      { name: 'Scallions', quantity: '1 bunch' },
      { name: 'Eggs', quantity: '2' },
      { name: 'Peanuts, roasted', quantity: '1/2 cup' },
      { name: 'Tamarind paste', quantity: '2 tbsp' },
      { name: 'Fish sauce', quantity: '2 tbsp' },
      { name: 'Brown sugar', quantity: '2 tbsp' },
      { name: 'Garlic', quantity: '3 cloves' },
      { name: 'Lime', quantity: '2' },
      { name: 'Neutral oil', quantity: '2 tbsp' },
      { name: 'Chili flakes', quantity: '1 tsp' },
    ],
    steps: [
      'Soak the rice noodles in hot water until pliable.',
      'Press and cube the tofu, then fry until golden.',
      'Whisk tamarind, fish sauce and brown sugar into a sauce.',
      'Scramble the eggs in the same pan.',
      'Toss noodles, sauce and sprouts over high heat.',
      'Finish with peanuts, scallions and lime.',
    ],
    origin: { kind: 'web', label: 'Weeknight Kitchen', url: 'https://recipes.example.com/veggie-pad-thai' },
  },
  // Sibling to the veggie version on purpose. The conflict veto in
  // lib/matchRecipes.js stops "chicken pad thai" being answered with the
  // vegetarian recipe; without a chicken one in the corpus the correct answer
  // would be "nothing found", which is honest but makes a working fix look
  // broken. Corpus, not logic.
  {
    id: 'chicken-pad-thai',
    dishName: 'Chicken pad thai',
    servings: 2,
    totalMinutes: 30,
    ingredients: [
      { name: 'Rice noodles', quantity: '8 oz' },
      { name: 'Chicken thighs, sliced', quantity: '12 oz' },
      { name: 'Bean sprouts', quantity: '2 cups' },
      { name: 'Scallions', quantity: '1 bunch' },
      { name: 'Eggs', quantity: '2' },
      { name: 'Peanuts, roasted', quantity: '1/2 cup' },
      { name: 'Tamarind paste', quantity: '2 tbsp' },
      { name: 'Fish sauce', quantity: '2 tbsp' },
      { name: 'Brown sugar', quantity: '2 tbsp' },
      { name: 'Garlic', quantity: '3 cloves' },
      { name: 'Lime', quantity: '2' },
      { name: 'Neutral oil', quantity: '2 tbsp' },
      { name: 'Chili flakes', quantity: '1 tsp' },
    ],
    steps: [
      'Soak the rice noodles in hot water until pliable.',
      'Sear the chicken over high heat until cooked through, then set aside.',
      'Whisk tamarind, fish sauce and brown sugar into a sauce.',
      'Scramble the eggs in the same pan.',
      'Toss noodles, chicken, sauce and sprouts over high heat.',
      'Finish with peanuts, scallions and lime.',
    ],
    origin: { kind: 'web', label: 'Weeknight Kitchen', url: 'https://recipes.example.com/chicken-pad-thai' },
  },
  {
    id: 'chicken-katsu-curry',
    dishName: 'Chicken katsu curry',
    servings: 4,
    totalMinutes: 45,
    prepMinutes: 15,
    cookMinutes: 30,
    ingredients: [
      { name: 'Chicken breast', quantity: '4' },
      { name: 'Panko breadcrumbs', quantity: '2 cups' },
      { name: 'Eggs', quantity: '2' },
      { name: 'Flour', quantity: '1 cup' },
      { name: 'Japanese curry roux', quantity: '1 box' },
      { name: 'Yellow onion', quantity: '1' },
      { name: 'Carrots', quantity: '2' },
      { name: 'Potatoes', quantity: '2' },
      { name: 'Jasmine rice', quantity: '2 cups' },
      { name: 'Chicken stock', quantity: '4 cups' },
      { name: 'Neutral oil', quantity: '2 cups' },
      { name: 'Salt', quantity: '1 tsp' },
      { name: 'Black pepper', quantity: '1 tsp' },
    ],
    steps: [
      'Start the rice.',
      'Simmer onion, carrot and potato in stock for 15 minutes.',
      'Melt the curry roux into the pot and thicken.',
      'Bread the chicken in flour, egg, then panko.',
      'Shallow-fry the cutlets 4 minutes a side.',
      'Slice the katsu and serve over rice with the curry.',
    ],
    origin: { kind: 'web', label: 'Table for Four', url: 'https://recipes.example.com/chicken-katsu-curry' },
  },
  {
    id: 'margherita-pizza',
    dishName: 'Margherita pizza',
    servings: 2,
    totalMinutes: 35,
    ingredients: [
      { name: 'Pizza dough', quantity: '1 lb' },
      { name: 'Canned San Marzano tomatoes', quantity: '14 oz' },
      { name: 'Fresh mozzarella', quantity: '8 oz' },
      { name: 'Basil', quantity: '1 bunch' },
      { name: 'Garlic', quantity: '2 cloves' },
      { name: 'Olive oil', quantity: '2 tbsp' },
      { name: 'Semolina flour', quantity: '2 tbsp' },
      { name: 'Salt', quantity: '1 tsp' },
    ],
    steps: [
      'Heat the oven as hot as it goes with a stone or steel inside.',
      'Crush the tomatoes with garlic and salt for the sauce.',
      'Stretch the dough on a semolina-dusted peel.',
      'Top thinly with sauce and torn mozzarella.',
      'Bake 6-8 minutes until the crust blisters.',
      'Finish with basil and olive oil.',
    ],
    origin: { kind: 'web', label: 'Slow Rise', url: 'https://recipes.example.com/margherita-pizza' },
  },
  {
    id: 'shrimp-fried-rice',
    dishName: 'Shrimp fried rice',
    servings: 3,
    totalMinutes: 20,
    ingredients: [
      { name: 'Cooked rice, chilled', quantity: '4 cups' },
      { name: 'Shrimp, peeled', quantity: '1 lb' },
      { name: 'Eggs', quantity: '3' },
      { name: 'Frozen peas and carrots', quantity: '1 cup' },
      { name: 'Scallions', quantity: '1 bunch' },
      { name: 'Garlic', quantity: '3 cloves' },
      { name: 'Soy sauce', quantity: '3 tbsp' },
      { name: 'Oyster sauce', quantity: '1 tbsp' },
      { name: 'Sesame oil', quantity: '1 tsp' },
      { name: 'Neutral oil', quantity: '2 tbsp' },
      { name: 'White pepper', quantity: '1/2 tsp' },
    ],
    steps: [
      'Scramble the eggs in a hot wok and set aside.',
      'Sear the shrimp 2 minutes, then set aside.',
      'Fry garlic, peas and carrots for 1 minute.',
      'Add the cold rice and press it into the wok to crisp.',
      'Return egg and shrimp, then sauce and toss.',
      'Finish with scallions and sesame oil.',
    ],
    origin: { kind: 'text', label: 'your description' },
  },
  {
    id: 'beef-chili',
    dishName: 'Classic beef chili',
    servings: 6,
    totalMinutes: 60,
    prepMinutes: 15,
    cookMinutes: 45,
    ingredients: [
      { name: 'Ground beef', quantity: '2 lb' },
      { name: 'Yellow onion', quantity: '2' },
      { name: 'Bell peppers', quantity: '2' },
      { name: 'Garlic', quantity: '4 cloves' },
      { name: 'Canned crushed tomatoes', quantity: '28 oz' },
      { name: 'Kidney beans', quantity: '2 cans' },
      { name: 'Tomato paste', quantity: '2 tbsp' },
      { name: 'Beef stock', quantity: '2 cups' },
      { name: 'Chili powder', quantity: '3 tbsp' },
      { name: 'Ground cumin', quantity: '1 tbsp' },
      { name: 'Smoked paprika', quantity: '2 tsp' },
      { name: 'Dried oregano', quantity: '1 tsp' },
      { name: 'Olive oil', quantity: '2 tbsp' },
      { name: 'Salt', quantity: '2 tsp' },
      { name: 'Black pepper', quantity: '1 tsp' },
    ],
    steps: [
      'Brown the beef in batches and set aside.',
      'Soften the onion and peppers, then add garlic and spices.',
      'Stir in tomato paste and cook until it darkens.',
      'Return the beef with tomatoes, beans and stock.',
      'Simmer uncovered for 40 minutes, stirring now and then.',
      'Season to taste before serving.',
    ],
    origin: { kind: 'web', label: 'Table for Four', url: 'https://recipes.example.com/beef-chili' },
  },
  {
    id: 'lemon-herb-chicken',
    dishName: 'Lemon herb chicken',
    servings: 4,
    totalMinutes: 35,
    ingredients: [
      { name: 'Chicken thighs', quantity: '8' },
      { name: 'Lemon', quantity: '2' },
      { name: 'Garlic', quantity: '4 cloves' },
      { name: 'Fresh thyme', quantity: '1 bunch' },
      { name: 'Fresh rosemary', quantity: '2 sprigs' },
      { name: 'Baby potatoes', quantity: '1.5 lb' },
      { name: 'Olive oil', quantity: '3 tbsp' },
      { name: 'Butter', quantity: '2 tbsp' },
      { name: 'Salt', quantity: '2 tsp' },
      { name: 'Black pepper', quantity: '1 tsp' },
    ],
    steps: [
      'Heat the oven to 425F.',
      'Toss potatoes with oil, salt and pepper on a sheet pan.',
      'Rub the chicken with garlic, lemon zest and herbs.',
      'Roast skin-side up for 30 minutes until crisp.',
      'Finish with lemon juice and a knob of butter.',
    ],
    origin: { kind: 'text', label: 'your description' },
  },
  {
    id: 'creamy-chicken-pasta',
    dishName: 'Creamy chicken pasta',
    servings: 2,
    totalMinutes: 25,
    ingredients: [
      { name: 'Penne pasta', quantity: '1 lb' },
      { name: 'Chicken breast', quantity: '1 lb' },
      { name: 'Heavy cream', quantity: '1 pint' },
      { name: 'Parmesan, shredded', quantity: '5 oz' },
      { name: 'Garlic', quantity: '3 cloves' },
      { name: 'Parsley', quantity: '1 bunch' },
      { name: 'Olive oil', quantity: '2 tbsp' },
      { name: 'Butter', quantity: '1 tbsp' },
      { name: 'Salt', quantity: '1 tsp' },
      { name: 'Black pepper', quantity: '1 tsp' },
    ],
    steps: [
      'Boil a pot of salted water.',
      'Cook the pasta for 10 minutes, reserving a cup of the water.',
      'Sear the chicken until golden, then slice.',
      'Soften the garlic in butter and add the cream.',
      'Melt in the Parmesan, loosening with pasta water.',
      'Toss with the pasta and chicken, finish with parsley.',
    ],
    origin: { kind: 'text', label: 'your description' },
  },
  {
    id: 'miso-ramen',
    dishName: 'Miso ramen',
    servings: 2,
    totalMinutes: 40,
    ingredients: [
      { name: 'Ramen noodles, fresh', quantity: '2 portions' },
      { name: 'Ground pork', quantity: '1/2 lb' },
      { name: 'White miso paste', quantity: '3 tbsp' },
      { name: 'Chicken stock', quantity: '4 cups' },
      { name: 'Soy milk', quantity: '1/2 cup' },
      { name: 'Garlic', quantity: '4 cloves' },
      { name: 'Ginger', quantity: '1 thumb' },
      { name: 'Eggs', quantity: '2' },
      { name: 'Corn kernels', quantity: '1/2 cup' },
      { name: 'Scallions', quantity: '1 bunch' },
      { name: 'Nori sheets', quantity: '2' },
      { name: 'Sesame oil', quantity: '1 tbsp' },
      { name: 'Neutral oil', quantity: '1 tbsp' },
    ],
    steps: [
      'Soft-boil the eggs for 7 minutes, then chill and peel.',
      'Brown the pork with garlic and ginger.',
      'Whisk the miso into the stock and bring to a simmer.',
      'Stir in the soy milk and keep it just below boiling.',
      'Cook the noodles separately and drain well.',
      'Bowl up the noodles, broth, pork, corn, egg and nori.',
    ],
    origin: { kind: 'video', label: 'Shorts', url: 'https://videos.example.com/shorts/miso-ramen' },
    media: { videoUrl: 'https://videos.example.com/shorts/miso-ramen' },
  },
  {
    id: 'garlic-veggie-stir-fry',
    dishName: 'Garlic veggie stir-fry',
    servings: 2,
    totalMinutes: 20,
    ingredients: [
      { name: 'Jasmine rice', quantity: '1 cup' },
      { name: 'Broccoli florets', quantity: '12 oz' },
      { name: 'Bell peppers', quantity: '2' },
      { name: 'Snap peas', quantity: '6 oz' },
      { name: 'Garlic', quantity: '1 head' },
      { name: 'Ginger', quantity: '1 thumb' },
      { name: 'Soy sauce', quantity: '3 tbsp' },
      { name: 'Cornstarch', quantity: '1 tsp' },
      { name: 'Sesame oil', quantity: '1 tsp' },
      { name: 'Neutral oil', quantity: '2 tbsp' },
      { name: 'Chili flakes', quantity: '1/2 tsp' },
    ],
    steps: [
      'Start the rice.',
      'Slice the peppers and mince the garlic and ginger.',
      'Stir-fry the vegetables over high heat for 6 minutes.',
      'Add garlic, ginger and soy sauce thickened with cornstarch.',
      'Toss for a minute and serve over the rice.',
    ],
    origin: { kind: 'web', label: 'Weeknight Kitchen', url: 'https://recipes.example.com/garlic-veggie-stir-fry' },
  },
  {
    id: 'carne-asada-tacos',
    dishName: 'Carne asada tacos',
    servings: 4,
    totalMinutes: 35,
    ingredients: [
      { name: 'Skirt steak', quantity: '1.5 lb' },
      { name: 'Corn tortillas', quantity: '12 ct' },
      { name: 'White onion', quantity: '1' },
      { name: 'Cilantro', quantity: '1 bunch' },
      { name: 'Lime', quantity: '4' },
      { name: 'Orange', quantity: '1' },
      { name: 'Garlic', quantity: '4 cloves' },
      { name: 'Ground cumin', quantity: '1 tsp' },
      { name: 'Neutral oil', quantity: '2 tbsp' },
      { name: 'Salt', quantity: '2 tsp' },
      { name: 'Black pepper', quantity: '1 tsp' },
    ],
    steps: [
      'Marinate the steak in citrus, garlic and cumin for 20 minutes.',
      'Get a grill or cast iron pan screaming hot.',
      'Sear the steak 3 minutes a side, then rest it.',
      'Chop the onion and cilantro.',
      'Slice the steak against the grain and fill warm tortillas.',
    ],
    origin: { kind: 'web', label: 'Sobremesa', url: 'https://recipes.example.com/carne-asada-tacos' },
  },
  {
    id: 'birria-tacos',
    dishName: 'Birria tacos',
    servings: 6,
    totalMinutes: 195,
    ingredients: [
      { name: 'Beef chuck', quantity: '3 lb' },
      { name: 'Guajillo chiles', quantity: '6' },
      { name: 'Ancho chiles', quantity: '3' },
      { name: 'Corn tortillas', quantity: '18 ct' },
      { name: 'Oaxaca cheese', quantity: '12 oz' },
      { name: 'White onion', quantity: '2' },
      { name: 'Garlic', quantity: '1 head' },
      { name: 'Roma tomatoes', quantity: '4' },
      { name: 'Beef stock', quantity: '6 cups' },
      { name: 'Bay leaves', quantity: '3' },
      { name: 'Cinnamon stick', quantity: '1' },
      { name: 'Whole cloves', quantity: '4' },
      { name: 'Mexican oregano', quantity: '1 tbsp' },
      { name: 'Ground cumin', quantity: '2 tsp' },
      { name: 'Apple cider vinegar', quantity: '2 tbsp' },
      { name: 'Cilantro', quantity: '1 bunch' },
      { name: 'Lime', quantity: '3' },
      { name: 'Salt', quantity: '1 tbsp' },
    ],
    steps: [
      'Toast and soak the dried chiles until soft.',
      'Blend the chiles with tomato, onion, garlic and spices.',
      'Sear the chuck, then braise it in the chile sauce for 3 hours.',
      'Shred the beef and skim the fat from the consomme.',
      'Dip tortillas in the fat, griddle with cheese and beef.',
      'Serve with consomme, cilantro, onion and lime.',
    ],
    origin: { kind: 'web', label: 'Sobremesa', url: 'https://recipes.example.com/birria-tacos' },
  },
  {
    id: 'sheet-pan-fajitas',
    dishName: 'Sheet-pan chicken fajitas',
    servings: 4,
    totalMinutes: 30,
    ingredients: [
      { name: 'Chicken breast', quantity: '1.5 lb' },
      { name: 'Bell peppers', quantity: '3' },
      { name: 'Yellow onion', quantity: '1' },
      { name: 'Flour tortillas', quantity: '8 ct' },
      { name: 'Chili powder', quantity: '1 tbsp' },
      { name: 'Ground cumin', quantity: '2 tsp' },
      { name: 'Garlic powder', quantity: '1 tsp' },
      { name: 'Lime', quantity: '2' },
      { name: 'Olive oil', quantity: '3 tbsp' },
      { name: 'Salt', quantity: '1.5 tsp' },
    ],
    steps: [
      'Heat the oven to 425F.',
      'Slice the chicken, peppers and onion into strips.',
      'Toss everything with oil, spices and salt on one sheet pan.',
      'Roast 20 minutes, stirring once.',
      'Squeeze over lime and serve with warm tortillas.',
    ],
    origin: { kind: 'web', label: 'Weeknight Kitchen', url: 'https://recipes.example.com/sheet-pan-fajitas' },
  },
  {
    id: 'chicken-shawarma-bowls',
    dishName: 'Chicken shawarma bowls',
    servings: 4,
    totalMinutes: 35,
    ingredients: [
      { name: 'Chicken thighs', quantity: '2 lb' },
      { name: 'Basmati rice', quantity: '2 cups' },
      { name: 'Greek yogurt', quantity: '1 cup' },
      { name: 'Garlic', quantity: '5 cloves' },
      { name: 'Lemon', quantity: '2' },
      { name: 'Cucumber', quantity: '1' },
      { name: 'Roma tomatoes', quantity: '2' },
      { name: 'Pickles', quantity: '1 jar' },
      { name: 'Ground cumin', quantity: '2 tsp' },
      { name: 'Ground coriander', quantity: '2 tsp' },
      { name: 'Smoked paprika', quantity: '2 tsp' },
      { name: 'Turmeric', quantity: '1 tsp' },
      { name: 'Olive oil', quantity: '3 tbsp' },
      { name: 'Salt', quantity: '2 tsp' },
    ],
    steps: [
      'Toss the chicken with the spices, lemon and oil.',
      'Start the rice.',
      'Sear the thighs 6 minutes a side until charred at the edges.',
      'Whisk yogurt with grated garlic and lemon for the sauce.',
      'Chop the cucumber and tomato.',
      'Slice the chicken and build bowls with rice, salad, pickles and sauce.',
    ],
    origin: { kind: 'web', label: 'Sobremesa', url: 'https://recipes.example.com/chicken-shawarma-bowls' },
  },
  {
    id: 'shakshuka',
    dishName: 'Shakshuka',
    servings: 2,
    totalMinutes: 25,
    ingredients: [
      { name: 'Eggs', quantity: '4' },
      { name: 'Canned crushed tomatoes', quantity: '28 oz' },
      { name: 'Yellow onion', quantity: '1' },
      { name: 'Red bell pepper', quantity: '1' },
      { name: 'Garlic', quantity: '3 cloves' },
      { name: 'Feta', quantity: '4 oz' },
      { name: 'Parsley', quantity: '1 bunch' },
      { name: 'Ground cumin', quantity: '1 tsp' },
      { name: 'Smoked paprika', quantity: '1 tsp' },
      { name: 'Olive oil', quantity: '2 tbsp' },
      { name: 'Salt', quantity: '1 tsp' },
      { name: 'Crusty bread', quantity: '1 loaf' },
    ],
    steps: [
      'Soften the onion and pepper in olive oil.',
      'Add garlic and spices, then the tomatoes.',
      'Simmer 10 minutes until thickened.',
      'Make wells and crack in the eggs.',
      'Cover and cook until the whites set.',
      'Scatter feta and parsley, serve with bread.',
    ],
    origin: { kind: 'web', label: 'Slow Rise', url: 'https://recipes.example.com/shakshuka' },
  },
  {
    id: 'thai-green-curry',
    dishName: 'Thai green curry',
    servings: 4,
    totalMinutes: 30,
    ingredients: [
      { name: 'Chicken thighs', quantity: '1.5 lb' },
      { name: 'Green curry paste', quantity: '4 tbsp' },
      { name: 'Coconut milk', quantity: '2 cans' },
      { name: 'Thai eggplant', quantity: '6 oz' },
      { name: 'Bamboo shoots', quantity: '1 can' },
      { name: 'Thai basil', quantity: '1 bunch' },
      { name: 'Fish sauce', quantity: '2 tbsp' },
      { name: 'Palm sugar', quantity: '1 tbsp' },
      { name: 'Kaffir lime leaves', quantity: '4' },
      { name: 'Jasmine rice', quantity: '2 cups' },
      { name: 'Neutral oil', quantity: '1 tbsp' },
    ],
    steps: [
      'Start the rice.',
      'Fry the curry paste in oil until fragrant.',
      'Add the thick part of the coconut milk and let it split.',
      'Stir in the chicken and cook through.',
      'Add the rest of the coconut milk, vegetables and seasoning.',
      'Simmer 10 minutes and finish with Thai basil.',
    ],
    origin: { kind: 'web', label: 'Table for Four', url: 'https://recipes.example.com/thai-green-curry' },
  },
  {
    id: 'beef-bourguignon',
    dishName: 'Sunday beef bourguignon',
    servings: 6,
    totalMinutes: 210,
    ingredients: [
      { name: 'Beef chuck', quantity: '3 lb' },
      { name: 'Bacon lardons', quantity: '6 oz' },
      { name: 'Red wine', quantity: '1 bottle' },
      { name: 'Beef stock', quantity: '3 cups' },
      { name: 'Pearl onions', quantity: '1 lb' },
      { name: 'Cremini mushrooms', quantity: '1 lb' },
      { name: 'Carrots', quantity: '4' },
      { name: 'Yellow onion', quantity: '2' },
      { name: 'Garlic', quantity: '6 cloves' },
      { name: 'Tomato paste', quantity: '2 tbsp' },
      { name: 'Flour', quantity: '3 tbsp' },
      { name: 'Butter', quantity: '4 tbsp' },
      { name: 'Olive oil', quantity: '2 tbsp' },
      { name: 'Fresh thyme', quantity: '1 bunch' },
      { name: 'Bay leaves', quantity: '2' },
      { name: 'Fresh parsley', quantity: '1 bunch' },
      { name: 'Brandy', quantity: '1/4 cup' },
      { name: 'Cognac', quantity: '2 tbsp' },
      { name: 'Egg noodles', quantity: '1 lb' },
      { name: 'Sugar', quantity: '1 tsp' },
      { name: 'Salt', quantity: '1 tbsp' },
      { name: 'Black pepper', quantity: '2 tsp' },
    ],
    steps: [
      'Render the lardons and set them aside.',
      'Sear the beef in batches until deeply browned.',
      'Soften the onion and carrot, then add garlic and tomato paste.',
      'Dust with flour, deglaze with brandy and the wine.',
      'Braise covered at 325F for 2.5 hours.',
      'Brown the pearl onions and mushrooms in butter separately.',
      'Fold everything together and serve over egg noodles.',
    ],
    origin: { kind: 'web', label: 'Slow Rise', url: 'https://recipes.example.com/beef-bourguignon' },
  },
];

export function findCatalogRecipe(id) {
  return RECIPE_CATALOG.find((recipe) => recipe.id === id) ?? null;
}
